import { ColorTypeButton } from '@/shared';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Card, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { esES } from '@mui/x-date-pickers/locales';
import 'dayjs/locale/es';
// import { color } from 'framer-motion';
import {
  MaterialReactTable,
  MRT_RowData,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_ES } from 'material-react-table/locales/es';
import React from 'react';

export type ChipColor = 
| 'default' 
| 'primary' 
| 'secondary'
| 'error'
| 'info'
| 'success'
| 'warning';

export type StatusDIsplayVariant = 'chip' | 'text' | 'dot';
export interface StatusConfig {
  label?: string;
  color?: ChipColor;
  textColor?: string;

}
export type StatusColorMap = Record<string, StatusConfig>;
export interface StatusColumnConfig {
  accessorKey: string;
  colorMap: StatusColorMap;
  displayVariant?: StatusDIsplayVariant;
}
export interface HeaderStyleConfig {
  backgroundColor?: string;
  textColor?: string;
  fontWeight?: string | number;
}


export interface GeneralTableColorsProps<T> {
  columns: any;
  data: T[];

  isLoading?: boolean;
  isRefetching?: boolean;

  ///* search
  enableGlobalFilter?: boolean;
  onGlobalFilterChange?: (value: any) => void;
  enableColumnFilters?: boolean;

  ///* paging
  pagination: any;
  onPaging: any;
  rowCount?: number;
  paginationDisplayMode?: 'custom' | 'default' | 'pages';

  ///* actions
  actionsColumnSize?: number;

  // crud
  canEdit?: boolean;
  canEditByRowData?: (original: T) => boolean;
  onEdit?: (original: T) => void;
  onCustomEdit?: (original: T) => React.ReactNode;

  canDelete?: boolean;
  onDelete?: (original: T) => void;

  canAdd?: boolean;
  onAdd?: (original: T) => void;
  //
  enableSorting?: boolean;

  //
  positionActionsColumn?: 'first' | 'last';

  //
  enableColumnActions?: boolean;
  enableEditing?: boolean;

  //
  enableManualFiltering?: boolean;
  onColumnFiltersChange?: (filters: any) => void;
  columnFilters?: any;
  columnFilterDisplayMode?: 'custom' | 'subheader' | 'popover';

  //select
  density?: 'compact' | 'comfortable' | 'spacious';
  enableTopToolbar?: boolean;

  editIcon?: any;
  toolTipTitleEditIcon?: string;
  editIconColor?: ColorTypeButton;

  showCustomActionBtnSpace?: boolean;
  customActionBtnSpace?: (original: T) => React.ReactNode;

  enableHoverEffect?: boolean;
  paintRow?: (original: T) => string;
  enableSelectAll?: boolean;
  enableRowSelection?: boolean;
  rowSelection?: any;
  setRowSelection?: any;
  getRowId?: (originalRow: T) => string;

  onSortingChange?: (data: any) => void
  sorting?: any
  manualSorting?: boolean
  pinLeftColumnIndexes?: number[],
  enablePagination?: boolean;

  darkHeader?: boolean;
  headerStyle?: HeaderStyleConfig;
  statusColumns?: StatusColumnConfig[];

}

function StatusCell({
  value,
  config,
}:{value:string, config:StatusColumnConfig}){
  const {colorMap, displayVariant = 'chip'} = config;
  const statusCfg: StatusConfig = colorMap[value] ?? {};
  const label = statusCfg.label ?? value;

  if( displayVariant === 'chip'){
    return (
      <Chip
        label = {label}
        color = {statusCfg.color ?? 'default'}
        size = "small"
        sx={{fontWeight: 'bold'}}
        />
    )
  }
  if(displayVariant==='dot'){
    return (
      <Box sx = {{display: 'flex', alignItems: 'center', gap: 0.8}}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: statusCfg.textColor ?? 'grey',
            flexShrink: 0,
          }}
          />
          <Typography
          variant='body2'
          fontWeight={'bold'}
          color={statusCfg.textColor ?? 'text.primary'}
          >
            {label}
          </Typography>
          </Box>
    );
  }
  return (
    <Typography
      variant='body2'
      fontWeight={"bold"}
      color={statusCfg.textColor ?? 'text.primary'}
    >
      {label}
    </Typography>
  )




}




function GeneralTableColors<T>({
  columns,
  data,

  isLoading = false,
  isRefetching = false,

  ///* search
  enableGlobalFilter = true,
  onGlobalFilterChange,
  enableColumnFilters = true,

  ///* paging
  pagination,
  onPaging,
  rowCount = 0,
  paginationDisplayMode = 'default',

  ///* actions
  actionsColumnSize = 90,
  canEdit = true,
  canEditByRowData = (v) => v ? true : false,
  onEdit,
  canDelete,
  onDelete,
  canAdd,
  onAdd,
  onCustomEdit = undefined,

  //
  enableSorting = false,

  //
  positionActionsColumn = 'first',

  //
  enableColumnActions = true,
  enableEditing = true,

  // filtering
  enableManualFiltering = false,
  onColumnFiltersChange,
  columnFilters,
  columnFilterDisplayMode = enableManualFiltering ? 'subheader' : 'popover',

  // select
  density = 'comfortable',
  editIcon,
  enableTopToolbar = true,

  toolTipTitleEditIcon = 'Editar',
  editIconColor,

  showCustomActionBtnSpace = false,
  customActionBtnSpace,

  enableHoverEffect = false,
  paintRow,
  enableSelectAll = false,
  enableRowSelection = false,
  rowSelection, setRowSelection,
  getRowId,
  onSortingChange,
  sorting,
  manualSorting = false,
  pinLeftColumnIndexes,
  enablePagination = true,

  darkHeader = false,
  headerStyle,
  statusColumns = []


}: Readonly<GeneralTableColorsProps<T>>) {
  const enhancedColumns = React.useMemo(() => {
    if(!statusColumns.length) return columns;
    return columns.map((col: any) => {
      const statusCfg = statusColumns.find(
        (sc) => sc.accessorKey === col.accessorKey
      );
      if(!statusCfg) return col;
      return {
        ...col,
        Cell: ({cell}: any) => (
          <StatusCell value={String(cell.getValue() ?? '')} config={statusCfg} />
        ),
      };
    });
  }, [columns, statusColumns]);

  const hasAnyAction = React.useMemo(() => {
  return !!(
    canEdit ||
    canDelete ||
    canAdd ||
    onCustomEdit ||
    showCustomActionBtnSpace
  );
}, [canEdit, canDelete, canAdd, onCustomEdit, showCustomActionBtnSpace]);

  const resolverdHeaderStyle = React.useMemo(():HeaderStyleConfig =>{
    const defaults: HeaderStyleConfig = darkHeader
    ? {backgroundColor: '#1e2330', textColor: '#ffffff', fontWeight: 'bold'}
    :{};
    return {...defaults, ...headerStyle};
  }, [darkHeader, headerStyle]);

  const headerSx = React.useMemo(()=>{
    if(!resolverdHeaderStyle.backgroundColor && !resolverdHeaderStyle.textColor)
      return undefined;
    return {
      backgroundColor: `${resolverdHeaderStyle.backgroundColor} !important`,
      color:`${ resolverdHeaderStyle.textColor} !important`,
      fontWeight: resolverdHeaderStyle.fontWeight ?? 'bold',
      '& .MuiTableSortLabel-root': {color: resolverdHeaderStyle.textColor},
      '& .MuiTableSortLabel-root.Mui-active': {color: resolverdHeaderStyle.textColor},
      '& svg': {color: resolverdHeaderStyle.textColor},
      '& .MuiIconButton-root': {color: resolverdHeaderStyle.textColor},
    };
  }, [resolverdHeaderStyle]);


  const columnPinning = React.useMemo(() => {

    const left: string[] = ['mrt-row-actions','mrt-row-select' ];

    pinLeftColumnIndexes?.forEach((i) => {
      const key = columns[i]?.accessorKey;
      if (key) left.push(key as string);
    });

    return { left };
  }, [rowSelection, pinLeftColumnIndexes, columns]);

  ///* defining table
  const table = useMaterialReactTable({
    // columns,
    columns: enhancedColumns,
    data: data as MRT_RowData[],
    ...(getRowId && { getRowId: (row) => getRowId(row as T) }),

    localization: MRT_Localization_ES,
    enableEditing: enableEditing && hasAnyAction,
    enableDensityToggle: true,
    enableFullScreenToggle: false,
    enableTopToolbar: enableTopToolbar,

    ...(enableManualFiltering && {
      manualFiltering: enableManualFiltering, // server-side filtering by columns
      onColumnFiltersChange: onColumnFiltersChange,
    }),

    ///* search
    enableGlobalFilter: enableGlobalFilter,
    positionGlobalFilter: 'left',
    // manualFiltering: true,
    onGlobalFilterChange: onGlobalFilterChange,
    enableColumnFilters: enableColumnFilters,
    columnFilterDisplayMode: columnFilterDisplayMode,

    ///* paging
    manualPagination: true,
    onPaginationChange: onPaging,
    rowCount: rowCount,
    muiPaginationProps: {
      showRowsPerPage: true,
    },

    paginationDisplayMode: paginationDisplayMode,

    ///* ordenar 
    manualSorting: manualSorting,
    //enableSorting: enableSorting,
    ...(enableSorting && { onSortingChange }),

    ///* actions - Solo incluir si enableColumnActions es true
    ...(enableColumnActions && hasAnyAction ? {
      enableColumnActions: true,
      positionActionsColumn: positionActionsColumn,
      displayColumnDefOptions: { 'mrt-row-actions': { size: actionsColumnSize } },
      renderRowActions: ({ row }) => {
        return (
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            {onCustomEdit ? (
              onCustomEdit(row.original as T)
            ) : (canEdit && canEditByRowData(row.original as T)) && (
              <Tooltip title={toolTipTitleEditIcon} placement='top'>
                <IconButton
                  onClick={() => {
                    onEdit && onEdit(row.original as T);
                  }}
                  color={editIconColor}
                >
                  {editIcon || <EditIcon />}
                </IconButton>
              </Tooltip>
            )}

            {canAdd && (
              <Tooltip title="Seleccionar">
                <IconButton
                  onClick={() => {
                    onAdd && onAdd(row.original as T);
                  }}
                >
                  {<AddCircleIcon color="success" />}
                </IconButton>
              </Tooltip>
            )}

            {canDelete && (
              <Tooltip title="Eliminar">
                <IconButton
                  onClick={() => {
                    onDelete && onDelete(row.original as T);
                  }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
            )}

            {showCustomActionBtnSpace && customActionBtnSpace
              ? customActionBtnSpace(row.original as T)
              : null}
          </Box>
        );
      }
    } : {
      enableColumnActions: false,
    }),

    renderTopToolbarCustomActions: () => (
      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
        Total de Registros: {rowCount}
      </Typography>
    ),

    muiTableBodyRowProps: ({ row }) => ({
      hover: enableHoverEffect,
      sx: {
        backgroundColor: paintRow ? paintRow(row.original as T) : undefined
      }
    }),

    ...(headerSx && {
      muiTableHeadCellProps: {sx: headerSx},
      muiTableHeadRowProps:{
        sx: { backgroundColor: `${resolverdHeaderStyle.backgroundColor} !important`},
      },
    }),
    //
    muiTablePaperProps: {
      elevation: 0,
    },

    ///* state compact, comfortable, spacious
    initialState: {
      showGlobalFilter: false,
      density: density,

    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },

    state: {
      ...(enableSorting && { sorting }),
      expanded: true,
      pagination,
      isLoading: isLoading || isRefetching,
      // filters
      ...(enableManualFiltering && { columnFilters }),
      // search bar
      //showLoadingOverlay: false,
      showProgressBars: isRefetching,
      //rowSelection
      ...(enableRowSelection && { rowSelection }),
      ...(columnPinning && { columnPinning }),
    },

    ...(enableRowSelection && {
      enableRowSelection: () => true,
      enableSelectAll: enableSelectAll,
      //enableRowSelection: enableRowSelection,
      onRowSelectionChange: setRowSelection,
      positionToolbarAlertBanner: 'top'
    }),
    enablePagination: enablePagination
  });

  return (
    <Card>
      <Box sx={{ width: '100%' }}>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          localeText={
            esES.components.MuiLocalizationProvider.defaultProps.localeText
          }
          adapterLocale="es"
        >
          <MaterialReactTable table={table} />
        </LocalizationProvider>
      </Box>
    </Card>
  );
}

export default GeneralTableColors;
